import { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useNavigationType } from 'react-router-dom';
import Header from '../../components/Header';
import BannerSlider from '../../components/Home/BannerSlider';
import BrowseCategories from '../../components/Home/BrowseCategories';
import Footer from '../../components/Footer';
import FearuredMerchants from '../../components/Home/FeaturedMerchants';
import NearbyMerchants from '../../components/Home/NearbyMerchants';
import MobileNavigation from '../../components/MobileNavigation';
import FeaturedProducts from '../../components/Home/FeaturedProducts';
import { useUser } from '../../contexts/user';
import { useTranslation } from 'react-i18next';
import log from '../../utils/logger';
import api from '../../api/api';
import NearbyMerchantsSkeleton from '../../components/Home/NearbyMerchantsSkeleton';
import { useScrollRestoration } from '../../hooks/useScrollRestoration';
import useToast from '../../hooks/useToast';
import Toast from '../../components/Toast';

const Home = () => {
  const { user, idToken, adminImpersonate } = useUser();
  const { i18n } = useTranslation();
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  useScrollRestoration(!loading);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const { toastMessage, toastType, showToast } = useToast();

  const navigationType = useNavigationType();
  // On POP navigation, mount Popular Brands eagerly so scroll restoration can
  // reach positions inside the section. Otherwise lazy-load when near viewport.
  const isBackNav = navigationType === 'POP';
  const { ref: nearbyRef, inView: nearbyInView } = useInView({
    triggerOnce: true,
    rootMargin: '200px',
    skip: isBackNav,
  });

  useEffect(() => {
    const storedLang = user?.language;
    if (storedLang) {
      i18n.changeLanguage(storedLang);
    }
  }, []);

  useEffect(() => {
    const fetchMerchants = async () => {
      try {
        const res = await api.get('/buyer/store');
        const data = res.data?.response?.data;
        if (Array.isArray(data)) {
          setMerchants(data);
        } else {
          setMerchants([]);
        }
      } catch (err) {
        log.error('Failed to fetch merchants:', err.message);
        setMerchants([]);
      } finally {
        setLoading(false);
      }
    };
  
    fetchMerchants();
  }, []);

  useEffect(() => {
    const fetchFeaturedProducts = async () => {
      try {
        const res = await api.get('/buyer/store/featuredProducts', {
          headers: user && idToken
            ? { Authorization: `Bearer ${idToken}` }
            : {},
          params: {
            adminImpersonateToken: adminImpersonate || null,
          },
        });
        const data = res.data?.response?.data;

        if (Array.isArray(data)) {
          setFeaturedProducts(data);
        } else {
          setFeaturedProducts([]);
        }
      } catch (err) {
        log.error('Failed to fetch featured products:', err.message);
        setFeaturedProducts([]);
      }
    };
  
    fetchFeaturedProducts();
  }, [idToken]);

  useEffect(() => {
    const toast = sessionStorage.getItem('toast');

    if (toast) {
      const { message, type } = JSON.parse(toast);
      showToast(message, type);
      sessionStorage.removeItem('toast');
    }
  }, []);

  return (
    <div className='mb-20 lg:mb-0'>
      <Header />
      <main className="min-h-dvh space-y-4 lg:space-y-6 pb-8">
        {loading ? (
          <NearbyMerchantsSkeleton />
        ) : (
          <>
            <MobileNavigation />
            <BannerSlider merchants={merchants} />
            <BrowseCategories />
            <FearuredMerchants merchants={merchants} />
            <FeaturedProducts groups={featuredProducts} />
            <div ref={nearbyRef} className="min-h-[300px]">
              {(isBackNav || nearbyInView) ? (
                <NearbyMerchants merchants={merchants} />
              ) : (
                <NearbyMerchantsSkeleton count={6} />
              )}
            </div>
          </>)}
      </main>
      <Footer />
      <Toast message={toastMessage} type={toastType} />
    </div>
  );
};

export default Home;